import { Matrix3, Quaternion, Ray, Vector2, Vector3 } from 'three';
import { Subject } from 'rxjs';
//const LOG_VECTOR_EPSILON = 5 as const; // VECTOR_EPSILON = 10E-(LOG_VECTOR_EPSILON)
const VECTOR_EPSILON = 0.00001; // Prevents floating point errors in vector equality
const MAX_RECURSION = 385; // Max number of times a piece can "chain" moves (e.g. rook moving 2x forward)
const BaseVector = {
    Zero: new Vector3(0, 0, 0),
    X: new Vector3(1, 0, 0),
    Y: new Vector3(0, 1, 0),
    Z: new Vector3(0, 0, 1),
    Zero2: new Vector2(0, 0),
    One2: new Vector2(1, 1),
    Eight2: new Vector2(8, 8)
};
//const ZeroGeometry = new THREE.BufferGeometry();
//const ZeroMaterial = new THREE.MeshPhysicalMaterial();
// region Utils
Vector3.prototype.equals = function (v) {
    if (VECTOR_EPSILON === undefined) {
        return ((v.x === this.x) && (v.y === this.y) && (v.z === this.z));
    }
    else {
        return ((Math.abs(v.x - this.x) <= VECTOR_EPSILON) && (Math.abs(v.y - this.y) <= VECTOR_EPSILON) && (Math.abs(v.z - this.z) <= VECTOR_EPSILON));
    }
};
Vector2.prototype.equals = function (v) {
    if (VECTOR_EPSILON === undefined) {
        return ((v.x === this.x) && (v.y === this.y));
    }
    else {
        return ((Math.abs(v.x - this.x) <= VECTOR_EPSILON) && (Math.abs(v.y - this.y) <= VECTOR_EPSILON));
    }
};
function reverseRecord(input) {
    return Object.fromEntries(Object.entries(input).map(([key, value]) => [
        value,
        key,
    ]));
}
// region Sides
// +x, -x, +y, -y, +z, -z
const Side = {
    Right: 0,
    Left: 1,
    Top: 2,
    Bottom: 3,
    Front: 4,
    Back: 5
};
const SideVecs = {
    [Side.Right]: { norm: new Vector3(1, 0, 0), file: new Vector3(0, 0, -1), rank: new Vector3(0, 1, 0) },
    [Side.Left]: { norm: new Vector3(-1, 0, 0), file: new Vector3(0, 0, 1), rank: new Vector3(0, 1, 0) },
    [Side.Front]: { norm: new Vector3(0, 0, 1), file: new Vector3(1, 0, 0), rank: new Vector3(0, 1, 0) },
    [Side.Back]: { norm: new Vector3(0, 0, -1), file: new Vector3(-1, 0, 0), rank: new Vector3(0, 1, 0) },
    [Side.Top]: { norm: new Vector3(0, 1, 0), file: new Vector3(1, 0, 0), rank: new Vector3(0, 0, -1) },
    [Side.Bottom]: { norm: new Vector3(0, -1, 0), file: new Vector3(1, 0, 0), rank: new Vector3(0, 0, 1) }
};
const SideAdjacencyGraph = {
    [Side.Right]: { left: Side.Front, right: Side.Back, up: Side.Top, down: Side.Bottom },
    [Side.Left]: { left: Side.Back, right: Side.Front, up: Side.Top, down: Side.Bottom },
    [Side.Front]: { left: Side.Left, right: Side.Right, up: Side.Top, down: Side.Bottom },
    [Side.Back]: { left: Side.Right, right: Side.Left, up: Side.Top, down: Side.Bottom },
    [Side.Top]: { left: Side.Left, right: Side.Right, up: Side.Back, down: Side.Front },
    [Side.Bottom]: { left: Side.Left, right: Side.Right, up: Side.Front, down: Side.Back }
};
// endregion Sides
// region Pieces
const Category = {
    Pawn: 0,
    Knight: 1,
    Bishop: 2,
    Rook: 3,
    Queen: 4,
    King: 5
};
const Color = {
    White: 0,
    Black: 1
};
// endregion Pieces
// region Annotations
const AnnotationVariant = {
    RecentMove: 0,
    PossibleMove: 1,
    Check: 2,
    SelectedPiece: 3,
    Highlight: 4,
    DangerousPossibleMove: 5,
    ProspectiveMove: 6
};
// endregion Annotations
// region Position and Vectors
const CardinalDirection = {
    Up: new Vector2(0, 1),
    Down: new Vector2(0, -1),
    Right: new Vector2(1, 0),
    Left: new Vector2(-1, 0)
};
const DiagonalDirection = {
    UpRight: new Vector2(1, 1),
    UpLeft: new Vector2(-1, 1),
    DownRight: new Vector2(1, -1),
    DownLeft: new Vector2(-1, -1)
};
const Direction = {
    ...CardinalDirection,
    ...DiagonalDirection
};
const SurfacePos = Vector2;
const UCISide = {
    [Side.Right]: 'o',
    [Side.Left]: 'r',
    [Side.Front]: 'g',
    [Side.Back]: 'b',
    [Side.Top]: 'w',
    [Side.Bottom]: 'y'
};
const ReverseUCISide = reverseRecord(UCISide);
const UCIFile = {
    1: 'a',
    2: 'b',
    3: 'c',
    4: 'd',
    5: 'e',
    6: 'f',
    7: 'g',
    8: 'h'
};
const ReverseUCIFile = reverseRecord(UCIFile);
const UCIRank = {
    1: '1',
    2: '2',
    3: '3',
    4: '4',
    5: '5',
    6: '6',
    7: '7',
    8: '8'
};
const ReverseUCIRank = reverseRecord(UCIRank);
const UCIPiece = {
    [Category.Pawn]: { [Color.White]: 'P', [Color.Black]: 'p' },
    [Category.Knight]: { [Color.White]: 'N', [Color.Black]: 'n' },
    [Category.Bishop]: { [Color.White]: 'B', [Color.Black]: 'b' },
    [Category.Rook]: { [Color.White]: 'R', [Color.Black]: 'r' },
    [Category.Queen]: { [Color.White]: 'Q', [Color.Black]: 'q' },
    [Category.King]: { [Color.White]: 'K', [Color.Black]: 'k' },
};
const ReverseUCIPiece = {
    'P': { category: Category.Pawn, color: Color.White },
    'p': { category: Category.Pawn, color: Color.Black },
    'N': { category: Category.Knight, color: Color.White },
    'n': { category: Category.Knight, color: Color.Black },
    'B': { category: Category.Bishop, color: Color.White },
    'b': { category: Category.Bishop, color: Color.Black },
    'R': { category: Category.Rook, color: Color.White },
    'r': { category: Category.Rook, color: Color.Black },
    'Q': { category: Category.Queen, color: Color.White },
    'q': { category: Category.Queen, color: Color.Black },
    'K': { category: Category.King, color: Color.White },
    'k': { category: Category.King, color: Color.Black },
};
const FEN6Piece = UCIPiece;
const ReverseFEN6Piece = ReverseUCIPiece;
const FEN6Color = {
    [Color.White]: 'w',
    [Color.Black]: 'b',
};
const ReverseFEN6Color = reverseRecord(FEN6Color);
/*function isLikeDirection(move: DeltaMove) {
    return (-1 - VECTOR_EPSILON <= move.x && move.x <= 1 + VECTOR_EPSILON) && (-1 - VECTOR_EPSILON <= move.y && move.y <= 1 + VECTOR_EPSILON) && !(move.equals(BaseVector.Zero));
}
function isLikeCardinalDirection(move: DeltaMove) {
    return isLikeDirection(move) && ((Math.abs(move.x) <= VECTOR_EPSILON) != (Math.abs(move.y) <= VECTOR_EPSILON));
}
function isLikeDiagonalDirection(move: DeltaMove) {
    return isLikeDirection(move) && ((Math.abs(move.x) <= VECTOR_EPSILON) == (Math.abs(move.y) <= VECTOR_EPSILON));
}*/
class Pos {
    #side;
    #surfacePos = new SurfacePos(1, 1);
    #posvec = new Ray();
    constructor(side, fileOrSurfacePos, rank) {
        if (side == undefined || fileOrSurfacePos == undefined) {
            side = Side.Right;
            fileOrSurfacePos = new SurfacePos(0, 0);
        }
        this.side = side;
        if (fileOrSurfacePos instanceof SurfacePos)
            this.surfacePos = fileOrSurfacePos;
        else {
            this.file = fileOrSurfacePos;
            this.rank = rank;
        }
        //console.log(this.posvec);
    }
    get side() {
        return this.#side;
    }
    set side(side) {
        this.#side = side;
        // this.recalculatePosvec();
    }
    get file() {
        return this.#surfacePos.x;
    }
    set file(file) {
        this.#surfacePos.x = Math.round(file);
        // this.recalculatePosvec();
    }
    get rank() {
        return this.#surfacePos.y;
    }
    set rank(rank) {
        this.#surfacePos.y = Math.round(rank);
        // this.recalculatePosvec();
    }
    get surfacePos() {
        return this.#surfacePos;
    }
    set surfacePos(surfacePos) {
        this.file = surfacePos.x; // let the individual setters deal with rounding
        this.rank = surfacePos.y;
    }
    get uci() {
        return `${UCISide[this.side]}${UCIFile[this.file]}${UCIRank[this.rank]}`;
    }
    set uci(uciPos) {
        if (uciPos.length != 3)
            throw Error('UCIPos has invalid length!');
        this.side = ReverseUCISide[uciPos[0]];
        this.file = ReverseUCIFile[uciPos[1]];
        this.rank = ReverseUCIRank[uciPos[2]];
    }
    get posvec() {
        let sv = SideVecs[this.side];
        return this.#posvec.set(sv.norm.clone().multiplyScalar(4)
            .add(sv.file.clone().multiplyScalar(this.file - 9 / 2))
            .add(sv.rank.clone().multiplyScalar(this.rank - 9 / 2)), sv.norm);
        // return {
        //     pos: this.#posvecpos // no async here so theoretically no race condition
        //         .copy(sv.norm.clone().multiplyScalar(SQUARE_WIDTH * 4))
        //         .add(sv.file.clone().multiplyScalar(this.file - SQUARE_WIDTH * 9 / 2))
        //         .add(sv.rank.clone().multiplyScalar(this.rank - SQUARE_WIDTH * 9 / 2)),
        //     vec: this.#posvecvec.copy(sv.norm)
        // };
    }
    /*canMove(move: DeltaMove): boolean {
        return this.canSurfaceMove(move) || (isLikeDirection(move) && this.canStep(move as Direction))
    }
    move(move: DeltaMove) {
        if (!this.canMove(move)) throw Error('Cannot move in such a direction! Please check with canMove first.');
    }*/
    static getClosestSide(pos3) {
        return Object.values(Side).map((side) => [side, SideVecs[side].norm.distanceTo(pos3)]).sort(((a, b) => a[1] - b[1]))[0][0];
    }
    set({ side, surfacePos, file, rank } = {}) {
        if (side != undefined)
            this.side = side;
        if (surfacePos != undefined)
            this.surfacePos = surfacePos;
        else {
            if (file)
                this.file = file;
            if (rank)
                this.rank = rank;
        }
        //this.#calculatePosVec();
        return this;
    }
    setSurfacePosFromPos3(pos3) {
        const newSide = Pos.getClosestSide(pos3);
        const newSv = SideVecs[newSide];
        let k = pos3.applyMatrix3(new Matrix3().set(newSv.norm.x, newSv.file.x, newSv.rank.x, newSv.norm.y, newSv.file.y, newSv.rank.y, newSv.norm.z, newSv.file.z, newSv.rank.z).invert());
        k.y += 9 / 2;
        k.z += 9 / 2;
        k.round();
        if (Math.abs(k.x - 4) > VECTOR_EPSILON
            || !(1 - VECTOR_EPSILON <= k.y && k.y <= 8 + VECTOR_EPSILON)
            || !(1 - VECTOR_EPSILON <= k.z && k.z <= 8 + VECTOR_EPSILON)) {
            throw Error('Failed to express PosVec in rotated basis');
        }
        this.set({ side: newSide, file: k.y, rank: k.z });
        return this;
    }
    canSurfaceMove(move) {
        //console.log('canSurfaceMove');
        //console.log(this.side, this.file, this.rank, move);
        return (1 - VECTOR_EPSILON <= this.file + move.x && this.file + move.x <= 8 + VECTOR_EPSILON)
            && (1 - VECTOR_EPSILON <= this.rank + move.y && this.rank + move.y <= 8 + VECTOR_EPSILON);
    }
    surfaceMove(move) {
        if (!this.canSurfaceMove(move))
            throw Error('Surface-bound move extends past edge of side!');
        this.surfacePos.add(move);
    }
    canCross(move) {
        return ((1 - VECTOR_EPSILON <= this.file + move.x && this.file + move.x <= 8 + VECTOR_EPSILON)
            || (1 - VECTOR_EPSILON <= this.rank + move.y && this.rank + move.y <= 8 + VECTOR_EPSILON))
            && ((-1 - VECTOR_EPSILON <= move.x && move.x <= 1 + VECTOR_EPSILON)
                && (-1 - VECTOR_EPSILON <= move.y && move.y <= 1 + VECTOR_EPSILON));
    }
    cross(direction) {
        //console.log(this.side, this.surfacePos, this.posvec.origin);
        //this.#calculatePosVec();
        //console.log(this.side, this.surfacePos, this.posvec.origin);
        let newPos = this.surfacePos.clone().add(direction);
        //console.log(newPos);
        let newSide;
        if (newPos.y >= 9 - VECTOR_EPSILON)
            newSide = SideAdjacencyGraph[this.side].up;
        else if (newPos.y <= VECTOR_EPSILON)
            newSide = SideAdjacencyGraph[this.side].down;
        else if (newPos.x >= 9 - VECTOR_EPSILON)
            newSide = SideAdjacencyGraph[this.side].right;
        else if (newPos.x <= VECTOR_EPSILON)
            newSide = SideAdjacencyGraph[this.side].left;
        else
            throw Error(`Something weird happened with vector math (floating point error?): <${newPos.x}, ${newPos.y}>`);
        //console.log(this.side, newSide);
        let sv = SideVecs[this.side];
        let quaternion = new Quaternion();
        quaternion.setFromUnitVectors(sv.norm.clone().normalize(), SideVecs[newSide].norm.clone().normalize());
        //console.log(quaternion)
        let delta3 = (sv.file.clone()).multiplyScalar(direction.x).add((sv.rank.clone()).multiplyScalar(direction.y));
        delta3.divideScalar(2);
        let pos = this.posvec.origin.clone();
        //console.log(pos)
        pos.add(delta3);
        //console.log(delta3)
        //console.log(pos)
        delta3.applyQuaternion(quaternion);
        //console.log(delta3)
        pos.add(delta3);
        //console.log(pos)
        delta3.multiplyScalar(2);
        const newSv = SideVecs[newSide];
        this.setSurfacePosFromPos3(pos);
        //console.log(this.side, this.rank, this.file);
        //console.log(this);
        return new Vector2(delta3.dot(newSv.file), delta3.dot(newSv.rank));
    }
    canSlide(move) {
        return this.canSurfaceMove(move) || this.canCross(move);
    }
    slide(move) {
        //console.log(move, this.surfacePos, this.posvec);
        //this.#calculatePosVec();
        if (this.canSurfaceMove(move)) {
            this.surfaceMove(move);
            return move;
        }
        else {
            if (this.canCross(move))
                return this.cross(move);
            throw Error('Can neither surfaceMove nor cross!');
        }
    }
    equals(other) {
        //return (this.posvec.origin.equals(other.posvec.origin)) && (this.posvec.direction.equals(other.posvec.direction));
        return (Math.abs(this.side - other.side) <= VECTOR_EPSILON && this.surfacePos.equals(other.surfacePos));
    }
}
class Move {
    piece;
    from = new Pos();
    to = new Pos();
    constructor(piece, to) {
        this.piece = piece;
        this.from.set(this.piece.pos);
        this.to.set(to);
    }
    get uci() {
        return `${this.from.uci}${this.to.uci}`;
    }
    set uci(uciMove) {
        if (uciMove.length != 7)
            throw Error('UCIPos has invalid length!');
        this.piece.uci = uciMove[0];
        this.from.uci = uciMove.slice(1, 4);
        this.to.uci = uciMove.slice(4);
    }
}
// endregion Position and Vectors
// endregion Utils
//region Rules
const GameVariant = {
    Standard: 0
};
const MotionRestriction = {
    // Piece cannot go past the edge of the board
    NoCross: 0,
    // Piece cannot have moved before this move
    FirstMove: 1,
    // DeltaMove can only be used to capture pieces; exclusive w/NoCapture
    Capture: 2,
    CaptureOwnColor: 3,
    CaptureOtherColor: 4,
    // DeltaMove cannot be used to capture pieces (phasing not affected)
    NoCapture: 5,
    NoCaptureOwnColor: 6,
    NoCaptureOtherColor: 7,
    // Once a blocking piece is encountered, the piece can't go beyond it; only relevant if repeat > 1
    NoPhase: 8,
    NoPhaseOwnColor: 9,
    NoPhaseOtherColor: 10,
    // King-specific moves
    /*NoRecieveCheck: 11,
    // TODO: Decide if NoGiveCheck should be added or not*/
    // Resolved: Do not allow the player to walk into check, but only end the game once the king is captured
    // Moves exclusive to a color (e.g. pawns moving in opposite directions)
    White: 12,
    Black: 13
};
const GameState = {
    Running: 0,
    KingCaptured: 1,
    Stalemate: 2
};
const Rulebook = {
    [GameVariant.Standard]: {
        isRestrictionLegal(board, piece, startingPos, move, restriction) {
            const newPos = new Pos(startingPos.side, startingPos.surfacePos);
            if (!newPos.canSlide(move))
                return { invalid: true, halt: true };
            newPos.slide(move);
            let kv = board.getPiece(newPos);
            if (restriction == MotionRestriction.NoCross) {
                if (!startingPos.canSurfaceMove(move))
                    return { invalid: true, halt: true };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.FirstMove) {
                if (piece.moved)
                    return { invalid: true, halt: true };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.Capture) {
                if (!kv[1])
                    return { invalid: true, halt: false };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.CaptureOtherColor) {
                if (!kv[1] || kv[1].variant.color == piece.variant.color)
                    return { invalid: true, halt: false };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.CaptureOwnColor) {
                if (!kv[1] || kv[1].variant.color == piece.variant.color)
                    return { invalid: true, halt: false };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.NoCapture) {
                if (kv[1])
                    return { invalid: true, halt: false };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.NoCaptureOwnColor) {
                if (kv[1] && kv[1].variant.color == piece.variant.color)
                    return { invalid: true, halt: false };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.NoCaptureOtherColor) {
                if (kv[1] && kv[1].variant.color != piece.variant.color)
                    return { invalid: true, halt: false };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.NoPhase) {
                if (kv[1])
                    return { invalid: false, halt: true };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.NoPhaseOwnColor) {
                if (kv[1] && kv[1].variant.color == piece.variant.color)
                    return { invalid: false, halt: true };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.NoPhaseOtherColor) {
                if (kv[1] && kv[1].variant.color != piece.variant.color)
                    return { invalid: false, halt: true };
                else
                    return { invalid: false, halt: false };
            }
            /*else if (restriction == MotionRestriction.NoRecieveCheck) {
                if (this.isAttacked(board, piece, newPos)) return {invalid: true, halt: false};
                else return {invalid: false, halt: false};
            }*/
            else if (restriction == MotionRestriction.White) {
                if (piece.variant.color != Color.White)
                    return { invalid: true, halt: true };
                else
                    return { invalid: false, halt: false };
            }
            else if (restriction == MotionRestriction.Black) {
                if (piece.variant.color != Color.Black)
                    return { invalid: true, halt: true };
                else
                    return { invalid: false, halt: false };
            }
            return { invalid: true, halt: true };
        },
        enumeratePossibleMoves(board, piece, pos, ignore, avoid, mustHave) {
            let moves = [];
            for (let range of this.motionRanges[piece.variant.category]) {
                let moveSequence = [...range.move];
                let verdict = { invalid: false, halt: false };
                const tempPos = (new Pos(pos.side, pos.surfacePos));
                let restrictions = range.restrictions || [];
                if (ignore)
                    restrictions = restrictions.filter((e) => !ignore.includes(e));
                if ((avoid && avoid.some(restrictions.includes.bind(restrictions)))
                    || (mustHave && !mustHave.some(restrictions.includes.bind(restrictions)))) {
                    //console.log('continuing', avoid, restrictions);
                    continue;
                }
                const repeat = range.repeat != undefined ? (range.repeat > 0 ? range.repeat : MAX_RECURSION) : 1;
                //console.log(range.repeat, repeat);
                //let toAdd: Array<Pos> = [];
                let seen = [new Pos(tempPos.side, tempPos.surfacePos)];
                for (let j = 0; j < repeat; j++) {
                    let flag = false;
                    for (let i = 0; i < moveSequence.length; i++) {
                        //console.log(pos.surfacePos, tempPos.surfacePos, range, i);
                        verdict = restrictions
                            .map((restriction) => this.isRestrictionLegal(board, piece, tempPos, moveSequence[i], restriction))
                            .reduce((prev, e) => Object.fromEntries(Object.entries(prev).map(([key, value]) => [key, value || e[key]])), { invalid: false, halt: false });
                        //console.log(tempPos.surfacePos, moveSequence, verdict);
                        if (!tempPos.canSlide(moveSequence[i]) || (verdict.invalid && verdict.halt)) {
                            flag = true;
                            break;
                        }
                        let sv1 = SideVecs[tempPos.side];
                        // console.log(verdict);
                        // console.log(moveSequence[i], tempPos.side, tempPos.surfacePos, "before");
                        moveSequence[i] = tempPos.slide(moveSequence[i]);
                        let sv2 = SideVecs[tempPos.side];
                        //board.highlight(tempPos);
                        // console.log(moveSequence[i], tempPos.side, tempPos.surfacePos);
                        // debugger;
                        let quaternion = (new Quaternion()).setFromUnitVectors(sv1.norm.clone().normalize(), sv2.norm.clone().normalize());
                        for (let k = i + 1; k < moveSequence.length; k++) {
                            let delta3 = (sv1.file.clone()).multiplyScalar(moveSequence[k].x)
                                .add((sv1.rank.clone()).multiplyScalar(moveSequence[k].y))
                                .applyQuaternion(quaternion);
                            moveSequence[k] = new Vector2(delta3.dot(sv2.file), delta3.dot(sv2.rank));
                        }
                    }
                    if (flag) {
                        //console.log(2);
                        //console.log(tempPos, moveSequence, j);
                        //throw Error();
                        break;
                    }
                    //console.log(verdict, tempPos);
                    //debugger;
                    if (seen.some(e => tempPos.equals(e))) {
                        break;
                    }
                    if (!verdict.invalid) {
                        if (!moves.some(e => tempPos.equals(e))) {
                            //console.log('push');
                            moves.push(new Pos(tempPos.side, tempPos.surfacePos));
                        }
                    }
                    if (verdict.halt) {
                        break;
                    }
                    seen.push(new Pos(tempPos.side, tempPos.surfacePos));
                }
            }
            return moves;
        },
        isAttacked(board, piece, pos) {
            for (let other of board.board.values()) {
                if (other.variant.color != piece.variant.color
                    && this.enumeratePossibleMoves(board, other, other.pos, [MotionRestriction.Capture, MotionRestriction.CaptureOtherColor, MotionRestriction.NoCaptureOwnColor], [MotionRestriction.NoCapture, MotionRestriction.NoCaptureOtherColor])
                        .some(pos.equals.bind(pos))) {
                    return true;
                }
            }
            return false;
        },
        getGameState(game) {
            const sameColorRoyalPieces = game.board.royalPieces.filter((piece) => piece.variant.color == game.activeColor);
            //console.log(sameColorRoyalPieces);
            if (sameColorRoyalPieces.length <= 0)
                return GameState.KingCaptured;
            for (let piece of game.board.board.values()) {
                if (game.canMove(piece.pos)) {
                    return GameState.Running;
                }
            }
            return GameState.Stalemate;
        },
        motionRanges: {
            [Category.Pawn]: [
                {
                    move: [Direction.Up],
                    repeat: 2,
                    restrictions: [MotionRestriction.FirstMove, MotionRestriction.NoCapture, MotionRestriction.NoPhase, MotionRestriction.White]
                },
                { move: [Direction.Up], restrictions: [MotionRestriction.NoCapture, MotionRestriction.White] }, // Allow promotion, aka entering the top/bottom sides
                {
                    move: [Direction.UpRight],
                    restrictions: [MotionRestriction.CaptureOtherColor, MotionRestriction.White]
                },
                {
                    move: [Direction.UpLeft],
                    restrictions: [MotionRestriction.CaptureOtherColor, MotionRestriction.White]
                },
                {
                    move: [Direction.Down],
                    repeat: 2,
                    restrictions: [MotionRestriction.FirstMove, MotionRestriction.NoCapture, MotionRestriction.NoPhase, MotionRestriction.Black]
                },
                { move: [Direction.Down], restrictions: [MotionRestriction.NoCapture, MotionRestriction.Black] },
                {
                    move: [Direction.DownRight],
                    restrictions: [MotionRestriction.CaptureOtherColor, MotionRestriction.Black]
                },
                {
                    move: [Direction.DownLeft],
                    restrictions: [MotionRestriction.CaptureOtherColor, MotionRestriction.Black]
                }
            ],
            [Category.Knight]: [
                {
                    move: [Direction.Up, Direction.Up, Direction.Left],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Up, Direction.Up, Direction.Right],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Down, Direction.Down, Direction.Left],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Down, Direction.Down, Direction.Right],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Right, Direction.Right, Direction.Up],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Right, Direction.Right, Direction.Down],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Left, Direction.Left, Direction.Up],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Left, Direction.Left, Direction.Down],
                    restrictions: [MotionRestriction.NoCaptureOwnColor]
                }
            ],
            [Category.Bishop]: [
                {
                    move: [Direction.UpRight],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.UpLeft],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.DownRight],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.DownLeft],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                }
            ],
            [Category.Rook]: [
                {
                    move: [Direction.Up],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.Down],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.Right],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.Left],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                }
            ],
            [Category.Queen]: [
                {
                    move: [Direction.UpRight],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.UpLeft],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.DownRight],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.DownLeft],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.Up],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.Down],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.Right],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                },
                {
                    move: [Direction.Left],
                    repeat: -1,
                    restrictions: [MotionRestriction.NoCaptureOwnColor, MotionRestriction.NoPhase]
                }
            ],
            [Category.King]: [
                { move: [Direction.Up], restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor] },
                {
                    move: [Direction.Down],
                    restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Right],
                    restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.Left],
                    restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.UpRight],
                    restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.UpLeft],
                    restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.DownRight],
                    restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor]
                },
                {
                    move: [Direction.DownLeft],
                    restrictions: [MotionRestriction.NoPhase, MotionRestriction.NoCaptureOwnColor]
                }
            ]
        },
        startingColor: Color.White,
        nextColor(game) {
            if (game.activeColor == Color.White)
                return Color.Black;
            return Color.White;
        },
        startingFen6: 'pppppppp/8/8/8/8/8/8/PPPPPPPP|pppppppp/8/8/8/8/8/8/PPPPPPPP|rbbnnbbr/b6b/b6b/n3q2n/n2k3n/b6b/b6b/rbbnnbbr|RBBNNBBR/B6B/B6B/N2K3N/N3Q2N/B6B/B6B/RBBNNBBR|pppppppp/8/8/8/8/8/8/PPPPPPPP|pppppppp/8/8/8/8/8/8/PPPPPPPP:w'
    }
};
class PieceObserver {
    /**
     * AFTER any move is done!!
     */
    beforeInit(board, piece) {
    }
    beforeThisMove(board, piece) {
    }
    afterThisMove(board, piece) {
    }
    afterThisAdded(board, piece) {
    }
    beforeOtherMove(board, piece) {
    }
    afterOtherMove(board, piece) {
    }
    afterOtherAdded(board, piece) {
    }
    afterThisSetVariant(board, piece) {
    }
    afterOtherSetVariant(board, piece) {
    }
}
class PawnObserver extends PieceObserver {
    afterThisMove(board, piece) {
        piece.moved = true;
        if (piece.pos.side == Side.Top || piece.pos.side == Side.Bottom) {
            piece.moved = false;
            board.setPieceVariant(piece.pos, { category: Category.Queen, color: piece.variant.color });
        }
    }
    afterThisAdded(board, piece) {
        if (!((piece.pos.rank == 1 && piece.variant.color == Color.White)
            || (piece.pos.rank == 8 && piece.variant.color == Color.Black))) {
            piece.moved = true;
        }
        if (piece.pos.side == Side.Top || piece.pos.side == Side.Bottom) {
            piece.moved = false;
            board.setPieceVariant(piece.pos, { category: Category.Queen, color: piece.variant.color });
        }
    }
}
class KingObserver extends PieceObserver {
    #updateSquaresTimeout = setTimeout(() => {
    });
    afterThisMove(board, piece) {
        this.#updateSquares(board, piece);
    }
    afterOtherMove(board, piece) {
        this.#updateSquares(board, piece);
    }
    beforeInit(board, piece) {
        piece.royal = true;
    }
    beforeThisMove(board, piece) {
        board.unmarkChecked(piece.pos);
    }
    beforeOtherMove(board, piece) {
        this.beforeThisMove(board, piece);
    }
    afterThisAdded(board, piece) {
        this.#updateSquares(board, piece);
    }
    afterOtherAdded(board, piece) {
        this.#updateSquaresAsync(board, piece);
    }
    afterThisSetVariant(board, piece) {
        this.#updateSquares(board, piece);
    }
    afterOtherSetVariant(board, piece) {
        this.#updateSquares(board, piece);
    }
    #updateSquares(board, piece) {
        if (board.isAttacked(piece, piece.pos)) {
            board.markChecked(piece.pos);
        }
    }
    #updateSquaresAsync(board, piece) {
        clearTimeout(this.#updateSquaresTimeout);
        this.#updateSquaresTimeout = setTimeout(() => {
            this.#updateSquares(board, piece);
        });
    }
}
const PieceObservers = {
    [Category.Pawn]: new PawnObserver(),
    [Category.Knight]: new PieceObserver(),
    [Category.Bishop]: new PieceObserver(),
    [Category.Rook]: new PieceObserver(),
    [Category.Queen]: new PieceObserver(),
    [Category.King]: new KingObserver()
};
class Piece {
    events = {
        setVariant: new Subject(),
        setPos: new Subject()
    };
    // id: number;
    #variant;
    moved = false; // restrictions
    royal = false;
    alive = false; // For disposal reasons
    #pos = new Pos(); // Initialized by assignment to this.pos which is called in constructor
    constructor(pos, variant) {
        // this.id = Math.random();
        // this.graphics = new PieceGraphics(variant, graphicsConfig);
        this.variant = variant;
        // setInterval(()=>{console.log(this.pos.uci)},0);
        this.pos = pos;
    }
    get pos() {
        return this.#pos;
    }
    set pos(pos) {
        // console.log('setPos!', pos.uci);
        this.#pos.set(pos);
        this.events.setPos.next(pos);
    }
    get uci() {
        return UCIPiece[this.variant.category][this.variant.color];
    }
    set uci(uciPiece) {
        this.variant = ReverseUCIPiece[uciPiece];
    }
    get variant() {
        return this.#variant;
    }
    set variant(variant) {
        this.#variant = variant;
        this.events.setVariant.next(variant);
        // this.events.setPos.next(this.pos);
    }
    /**
     * NOTE: Use Board.movePiece instead!
     * @deprecated
     * @param move
     */
    _slide(move) {
        let out = this.pos.slide(move);
        //console.log('afterwards:', this.pos)
        this.events.setPos.next(this.pos);
        return out;
    }
}
class Annotation {
    events = {
        setVariant: new Subject(),
        setPos: new Subject()
    };
    // graphics: AnnotationGraphics
    variant;
    #pos = new Pos(); // Initialized indirectly in constructor
    constructor(pos, variant /*, graphicsConfig: GraphicsConfig*/) {
        // this.graphics = new AnnotationGraphics(variant, graphicsConfig);
        this.setVariant(variant);
        this.pos = pos;
        // console.log(pos.posvec.origin);
    }
    get pos() {
        return this.#pos;
    }
    set pos(pos) {
        this.#pos.set(pos);
        this.events.setPos.next(pos);
    }
    setVariant(variant) {
        this.variant = variant;
        this.events.setVariant.next(variant);
        // this.events.setPos.next(this.pos);
    }
}
class AnnotationGroup {
    #annotations;
    constructor(annotations) {
        this.#annotations = annotations;
    }
    get annotations() {
        return this.#annotations;
    }
    addAnnotation(annotation) {
        this.#annotations.push(annotation);
    }
    getAnnotations(pos) {
        return [...this.annotations].filter((annotation) => pos.equals(annotation.pos));
    }
    delAnnotation(annotation) {
        if (this.#annotations.includes(annotation))
            this.#annotations.splice(this.#annotations.indexOf(annotation), 1);
    }
}
class Board {
    events = {
        addPiece: new Subject(),
        addAnnotation: new Subject(),
        delPiece: new Subject(),
        delAnnotation: new Subject(),
    };
    board = new Map();
    annotationBoard = new Map();
    /**
     * Maps Object3D.id to logical object (e.g. This Board, Piece, Annotation, etc.)
     */
    meshBoard = new Map();
    royalPieces = [];
    rules; // TODO: Add game variants
    possibleMovesCache = new Map();
    boardCache = new Map();
    constructor(rules) {
        this.rules = rules;
        // this.graphics = new BoardGraphics(graphicsConfig);
        // this.graphicsConfig = graphicsConfig;
        // this.meshBoard.set(this.graphics.boardObject.id, this); // NOTE: boardObject is the one with collision, not object!
        //this.board = new Array(6).fill(undefined).map(u => new Array(8).fill(undefined).map(u => new Array(8).fill(undefined)))
    }
    // region Piece Management
    get fen6() {
        let pos = new Pos();
        let out = '';
        for (let side of Object.values(Side)) {
            for (let rank = 8; rank >= 1; rank--) {
                for (let file = 1; file <= 8; file++) {
                    pos.set({ side: side, file: file, rank: rank });
                    let piece = this.getPiece(pos)[1];
                    if (piece) {
                        out += FEN6Piece[piece.variant.category][piece.variant.color];
                    }
                    else {
                        if (!out.at(-1) || isNaN(Number(out.at(-1))))
                            out += '1';
                        else
                            out = out.slice(0, -1) + (Number(out.at(-1)) + 1).toString();
                    }
                }
                out += '/';
            }
            out = out.slice(0, -1); // Remove trailing delimiter
            out += '|';
        }
        return out.slice(0, -1); // Remove trailing delimiter
    }
    set fen6(fenBoard) {
        this.delAllPieces();
        let pos = new Pos();
        pos.side = 0;
        for (let fenSide of fenBoard.split('|')) {
            pos.rank = 8;
            for (let fenRow of fenSide.split('/')) {
                pos.file = 1;
                for (let fenChar of fenRow) {
                    if (isNaN(Number(fenChar))) {
                        //console.log(fenChar);
                        this.addPiece(pos, ReverseFEN6Piece[fenChar]);
                        if (pos.file >= 8)
                            break;
                        pos.file++;
                    }
                    else {
                        pos.file += Number(fenChar);
                    }
                }
                if (pos.rank <= 1)
                    break;
                pos.rank--;
            }
            if (pos.side >= 5)
                break;
            pos.side++;
        }
    }
    addPiece(pos, variant) {
        const piece = new Piece(pos, variant);
        this._addExternalPiece(pos, piece);
        return piece;
    }
    _addExternalPiece(pos, piece) {
        pos = new Pos(pos.side, pos.surfacePos);
        const kv = this.getPiece(pos);
        if (kv[0]) {
            this.delPiece(kv[0]);
        }
        // piece.pos = pos; // Just makign sure
        this.board.set(pos, piece);
        //console.log('piece"s pos')
        //console.log(piece.variant.category);
        PieceObservers[piece.variant.category].beforeInit(this, piece);
        if (piece.royal)
            this.royalPieces.push(piece);
        piece.alive = true;
        this.invalidateBoard();
        for (let selectedPiece of this.board.values()) {
            if (selectedPiece == piece)
                PieceObservers[selectedPiece.variant.category].afterThisAdded(this, selectedPiece);
            else
                PieceObservers[selectedPiece.variant.category].afterOtherAdded(this, selectedPiece);
        }
        this.events.addPiece.next(piece);
        //console.log(piece.pos.uci);
    }
    getPiece(pos) {
        let t = this.boardCache.get(pos);
        if (t)
            return [pos, t];
        const kv = [...this.board].filter(([key]) => pos.equals(key)).pop();
        if (!kv)
            return [undefined, undefined];
        this.boardCache.set(kv[0], kv[1]);
        return kv;
    }
    movePiece(pos, newPos) {
        newPos = new Pos(newPos.side, newPos.surfacePos);
        //console.log(newPos.surfacePos, newPos.side)
        const [k, v] = this.getPiece(pos);
        if (k == undefined || v == undefined)
            throw Error('No piece at Pos!');
        for (let piece of this.board.values()) {
            if (piece == v)
                PieceObservers[piece.variant.category].beforeThisMove(this, piece);
            else
                PieceObservers[piece.variant.category].beforeOtherMove(this, piece);
        }
        if (this.getPiece(newPos)[0])
            this.delPiece(newPos);
        this.board.set(newPos, v);
        //console.log('predelete', this.board.get(k), k.surfacePos);
        this.board.delete(k);
        //console.log('deleted', this.board.get(k));
        v.pos = newPos;
        this.invalidateBoard();
        //console.log(v.pos.surfacePos);
        for (let piece of this.board.values()) {
            if (piece == v)
                PieceObservers[piece.variant.category].afterThisMove(this, piece);
            else
                PieceObservers[piece.variant.category].afterOtherMove(this, piece);
        }
    }
    setPieceVariant(pos, variant) {
        const [k, v] = this.getPiece(pos);
        if (!v)
            throw Error('No piece at Pos!');
        v.variant = variant;
        for (let piece of this.board.values()) {
            if (piece == v)
                PieceObservers[piece.variant.category].afterThisSetVariant(this, piece);
            else
                PieceObservers[piece.variant.category].afterOtherSetVariant(this, piece);
        }
    }
    // endregion Piece Management
    // region Annotation Management
    delPiece(pos) {
        const [k, v] = this.getPiece(pos);
        if (k == undefined || v == undefined)
            throw Error('No piece at Pos!');
        // this.meshBoard.delete(v.graphics.object.id);
        let i = this.royalPieces.indexOf(v);
        if (i >= 0)
            this.royalPieces.splice(i, 1);
        this.board.delete(k);
        v.alive = false;
        this.invalidateBoard();
        this.events.delPiece.next(v);
    }
    delAllPieces() {
        for (let piece of [...this.board.values()]) {
            this.delPiece(piece.pos);
        }
    }
    getAnnotationGroup(groupPos) {
        const kv = [...this.annotationBoard].filter(([key]) => 
        /*groupPos === key
        || (groupPos instanceof Pos && key instanceof Pos && */ groupPos.equals(key) /*)*/).pop();
        if (kv)
            return kv;
        return [undefined, undefined];
    }
    /**
     * @param groupPos - What position the AnnotationGroup is linked to
     * @param annotationPos - Where the Annotation is actually rendered on the board
     * @param variant - Details about the Annotation
     */
    addAnnotation(groupPos, annotationPos, variant) {
        const annotation = new Annotation(annotationPos, variant);
        this._addExternalAnnotation(groupPos, annotation);
        return annotation;
    }
    _addExternalAnnotation(groupPos, annotation) {
        const kv = this.getAnnotationGroup(groupPos);
        //console.log('kv', kv);
        if (kv[1])
            kv[1].addAnnotation(annotation);
        else
            this.annotationBoard.set(groupPos, new AnnotationGroup([annotation]));
        // this.meshBoard.set(annotation.graphics.object.id, annotation);
        this.events.addAnnotation.next(annotation);
    }
    delAnnotation(groupPos, annotation) {
        const kv = this.getAnnotationGroup(groupPos);
        if (kv[1]) {
            //const annotation = kv[1].getAnnotation(pos);
            //if (annotation) {
            kv[1].delAnnotation(annotation);
            // this.meshBoard.delete(annotation.graphics.object.id);
            this.events.delAnnotation.next(annotation);
            return;
            //}
        }
        throw Error('No annotationGroup at Pos!');
    }
    delAllAnnotations(groupPos) {
        const kv = this.getAnnotationGroup(groupPos);
        if (kv[1]) {
            // @tds-ignore kv[1] is NOT undefined because I checked already (see previous line)
            for (let annotation of kv[1].annotations) {
                this.delAnnotation(groupPos, annotation);
            }
            // kv[1].annotations.forEach(annotation => {kv[1]?.delAnnotation(annotation)});
        }
        else
            throw Error('No annotationGroup at Pos!');
    }
    delAllAllAnnotations() {
        [...this.annotationBoard.keys()].forEach(this.delAllAnnotations.bind(this));
    }
    // recalculatePossibleMoves(pos1: Pos) {
    //     const [pos, piece] = this.getPiece(pos1);
    //     const [_, annotationGroup] = this.getAnnotationGroup(pos1);
    //     if (!pos || !piece || !annotationGroup) return;
    //     for (let annotation of annotationGroup.annotations.slice()) {
    //         if (this.isAttacked(piece, annotation.pos)) {
    //             annotation.setVariant(AnnotationVariant.DangerousPossibleMove);
    //         }
    //         else {
    //             annotation.setVariant(AnnotationVariant.PossibleMove);
    //         }
    //     }
    /**
     * Shows all legal moves
     * @param pos1
     * @param prospective - Optional; If `true`, then `AnnotationVariant.ProspectiveMove` is used
     */
    showPossibleMoves(pos1, prospective) {
        const [pos, piece] = this.getPiece(pos1);
        if (!pos || !piece)
            throw Error('No piece at Pos!');
        let destinations = this.enumeratePossibleMoves(pos);
        for (let destination of destinations) {
            if (prospective) {
                this.addAnnotation(pos, destination, AnnotationVariant.ProspectiveMove);
            }
            else if (this.isAttacked(piece, destination)) {
                this.addAnnotation(pos, destination, AnnotationVariant.DangerousPossibleMove);
            }
            else {
                this.addAnnotation(pos, destination, AnnotationVariant.PossibleMove);
            }
        }
        //console.log(...this.annotationBoard.values())
    }
    hidePossibleMoves(pos1) {
        const [pos, annotationGroup] = this.getAnnotationGroup(pos1);
        if (!pos || !annotationGroup)
            return; //throw Error('No annotationGroup at Pos!');
        for (let annotation of annotationGroup.annotations.slice()) {
            //console.log(annotation);
            if (annotation.variant == AnnotationVariant.PossibleMove
                || annotation.variant == AnnotationVariant.DangerousPossibleMove
                || annotation.variant == AnnotationVariant.ProspectiveMove) {
                this.delAnnotation(pos, annotation);
            }
        }
    }
    // }
    invalidateBoard() {
        this.possibleMovesCache.clear();
        this.boardCache.clear();
        // this.delAllAnnotations();
    }
    hideAllPossibleMoves() {
        for (let pos of this.annotationBoard.keys()) {
            this.hidePossibleMoves(pos);
        }
    }
    highlight(pos1) {
        this.addAnnotation(pos1, pos1, AnnotationVariant.Highlight);
    }
    unhighlight(pos1) {
        let annotations = this.getAnnotationGroup(pos1)[1]?.getAnnotations(pos1);
        if (annotations) {
            for (let annotation of annotations) {
                if (annotation.variant == AnnotationVariant.Highlight) {
                    this.delAnnotation(pos1, annotation);
                }
            }
        }
    }
    isHighlighted(pos) {
        return !!this.getAnnotationGroup(pos)[1]?.annotations.some(annotation => annotation.variant == AnnotationVariant.Highlight);
    }
    unhighlightAll() {
        for (let pos of this.annotationBoard.keys()) {
            this.unhighlight(pos);
        }
    }
    _showRecentMove(pos1) {
        this.addAnnotation(pos1, pos1, AnnotationVariant.RecentMove);
    }
    _hideRecentMove(pos1) {
        let annotations = this.getAnnotationGroup(pos1)[1]?.getAnnotations(pos1);
        if (annotations) {
            for (let annotation of annotations) {
                if (annotation.variant == AnnotationVariant.RecentMove) {
                    this.delAnnotation(pos1, annotation);
                }
            }
        }
    }
    showRecentMove(move) {
        // console.log('showrecentmove', move.from.uci, move.to.uci);
        this._showRecentMove(move.from);
        this._showRecentMove(move.to);
    }
    hideRecentMove(move) {
        // console.log('hiderecentmove', move.from.uci, move.to.uci);
        this._hideRecentMove(move.from);
        this._hideRecentMove(move.to);
    }
    /**
     * @deprecated
     */
    highlightMultiple(posarray) {
        for (let pos of posarray) {
            this.highlight(pos);
        }
    }
    /**
     * @deprecated
     */
    unhighlightMultiple(posarray) {
        for (let pos of posarray) {
            this.unhighlight(pos);
        }
    }
    select(pos1) {
        this.addAnnotation(pos1, pos1, AnnotationVariant.SelectedPiece);
    }
    unselect(pos1) {
        let annotations = this.getAnnotationGroup(pos1)[1]?.getAnnotations(pos1);
        if (annotations) {
            for (let annotation of annotations) {
                if (annotation.variant == AnnotationVariant.SelectedPiece) {
                    this.delAnnotation(pos1, annotation);
                }
            }
        }
    }
    // endregion Annotation Management
    markChecked(pos1) {
        this.addAnnotation(pos1, pos1, AnnotationVariant.Check);
    }
    unmarkChecked(pos1) {
        let annotations = this.getAnnotationGroup(pos1)[1]?.getAnnotations(pos1);
        if (annotations) {
            for (let annotation of annotations) {
                if (annotation.variant == AnnotationVariant.Check) {
                    this.delAnnotation(pos1, annotation);
                }
            }
        }
    }
    /**
     * Moves a piece from point A to point B
     * @param move - A Move object
     */
    move(move) {
        this.movePiece(move.from, move.to);
    }
    /**
     * Lists all legal moves a piece can make
     * @param piecePos - the Pos of the piece in question
     */
    enumeratePossibleMoves(piecePos) {
        const [pos, piece] = this.getPiece(piecePos);
        if (!pos || !piece)
            throw Error('No piece at Pos!');
        let res = this.possibleMovesCache.get(piece) || this.rules.enumeratePossibleMoves(this, piece, pos);
        this.possibleMovesCache.set(piece, res);
        return res;
    }
    // region Parsing
    isAttacked(piece, pos) {
        return this.rules.isAttacked(this, piece, pos);
    }
    /**
     * Checks if a move is legal for the piece at `move.from`
     * @param move
     */
    isPossibleMove(move) {
        return this.enumeratePossibleMoves(move.from).some(move.to.equals.bind(move.to));
    }
}
class Game {
    // graphics: GameGraphics
    events = {
    // updateThisColor: new Subject<Color>()
    // addBoard: new Subject()
    };
    board;
    variant;
    selected;
    // #thisColor!: Color;
    moveHistory = [];
    #activeColor;
    // #invalid!: boolean;
    /**
     * @param variant
     */
    constructor(variant) {
        // this.graphics = new GameGraphics();
        // this.thisColor = thisColor;
        this.variant = variant;
        this.activeColor = Rulebook[this.variant].startingColor;
        // this.invalid = true;
        this.board = new Board(Rulebook[this.variant]);
        // console.log('triggering addboard')
        // this.events.addBoard.next(this.board);
    }
    // get invalid(): boolean {
    //     return this.#invalid;
    // }
    // set invalid(value: boolean) {
    //     this.#invalid = value;
    // }
    get activeColor() {
        return this.#activeColor;
    }
    set activeColor(color) {
        this.#activeColor = color;
        // this.invalid = true;
        // this.events.setPerspective.next(this.#activeColor)
    }
    get recentMove() {
        return this.moveHistory.at(-1);
    }
    set recentMove(move) {
        this.moveHistory.push(move);
    }
    get fen6() {
        return `${this.board.fen6}:${FEN6Color[this.activeColor]}`;
    }
    set fen6(fenState) {
        if (!this.validateFen6(fenState))
            throw Error('Invalid FEN6State!');
        let split = fenState.split(':');
        let [fenBoard, fenColor] = split;
        this.activeColor = ReverseFEN6Color[fenColor];
        this.hideAllAnnotations();
        this.clearMoveHistory();
        this.board.fen6 = fenBoard;
    }
    advanceActiveColor() {
        this.activeColor = Rulebook[this.variant].nextColor(this);
        // this.thisColor = this.activeColor; // TODO: Only for pass & play
    }
    clearMoveHistory() {
        this.moveHistory = [];
    }
    /**
     * Determines if play can continue or is halted (e.g. no royal pieces left)
     */
    getGameState() {
        return Rulebook[this.variant].getGameState(this);
    }
    addPiece(pos, variant) {
        if (!this.board.getPiece(pos)[0]) {
            return this.board.addPiece(pos, variant);
        }
        throw Error('Cannot add a new piece on top of an existing piece!');
    }
    getPiece(pos) {
        return this.board.getPiece(pos)[1];
    }
    move(move) {
        this.board.hideAllPossibleMoves();
        this.board.unhighlight(move.from);
        this.board.unhighlight(move.to);
        if (this.recentMove)
            this.board.hideRecentMove(this.recentMove);
        this.unselect();
        if (!this.isPossibleMove(move)) { // At this point, we're just providing detailed error messages
            if (this.getGameState() != GameState.Running)
                throw Error('Pieces cannot be moved on a completed (gameOver-ed) board!');
            else if (!this.getPiece(move.from))
                throw Error('No piece at (starting) Pos!');
            else if (this.getPiece(move.from).variant.color != this.activeColor)
                throw Error('Cannot move out of turn!');
            else if (!this.board.isPossibleMove(move))
                throw Error('Not a possible move!');
            else
                throw Error('(Generic) not a possible move at this time!');
        }
        else
            this.board.move(move);
        this.recentMove = move;
        // console.log('postmove', move.from.uci);
        this.board.showRecentMove(move);
        this.advanceActiveColor();
    }
    enumeratePossibleMoves(piecePos) {
        return this.board.enumeratePossibleMoves(piecePos);
    }
    isPossibleMove(move) {
        return (this.getGameState() == GameState.Running)
            && (this.getPiece(move.from) != undefined)
            && (this.getPiece(move.from).variant.color == this.activeColor)
            && (this.board.isPossibleMove(move));
    }
    canMove(piecePos) {
        const piece = this.getPiece(piecePos);
        if (!piece)
            throw Error('No piece at Pos!');
        return piece.variant.color == this.activeColor && (!!this.board.enumeratePossibleMoves(piece.pos));
    }
    showPossibleMoves(piecePos) {
        this.board.showPossibleMoves(piecePos);
    }
    hidePossibleMoves(piecePos) {
        this.board.hidePossibleMoves(piecePos);
    }
    hideAllPossibleMoves() {
        this.board.hideAllPossibleMoves();
    }
    highlight(pos) {
        this.board.highlight(pos);
    }
    unhighlight(pos) {
        this.board.unhighlight(pos);
    }
    isHighlighted(pos) {
        return this.board.isHighlighted(pos);
    }
    unhighlightAll() {
        this.board.unhighlightAll();
    }
    select(pos) {
        this.unselect();
        this.selected = pos;
        let piece = this.board.getPiece(this.selected)[1];
        if (piece)
            this.board.showPossibleMoves(this.selected, piece.variant.color != this.activeColor);
        this.board.select(pos);
    }
    unselect() {
        if (this.selected) {
            this.board.hidePossibleMoves(this.selected);
            this.board.unselect(this.selected);
            this.selected = undefined;
        }
    }
    reset() {
        this.unselect();
        this.clearMoveHistory();
        this.hideAllAnnotations();
    }
    /**
     * Call this BEFORE clearMoveHistory();
     */
    hideAllAnnotations() {
        this.unselect();
        this.board.delAllAllAnnotations();
    }
    validateFen6(fenState) {
        let split = fenState.split(':');
        return split.length == 2
            && Object.values(FEN6Color).includes(split[1])
            && split[0].split('|').length == 6
            && !split[0].split('|').some(part => part.split('/').length != 8);
    }
}
// endregion Logic
// region Exports
export { Vector2, Vector3, Side, Category, Color, AnnotationVariant, CardinalDirection, DiagonalDirection, Direction, SurfacePos, UCISide, ReverseUCISide, UCIFile, ReverseUCIFile, UCIRank, ReverseUCIRank, UCIPiece, ReverseUCIPiece, FEN6Piece, ReverseFEN6Piece, FEN6Color, ReverseFEN6Color, Pos, Move, BaseVector, GameVariant, MotionRestriction, GameState, Rulebook, PieceObserver, PawnObserver, KingObserver, PieceObservers, Piece, Annotation, AnnotationGroup, Board, Game };
// endregion Exports
